import { meshNode, type MeshNode, updateMesh } from '@/lib/threeScene/nodes/mesh';
import type { PlannerViewState } from '@/views/3d-planner/store/plannerState';
import { stopAll, type StopHandle } from '@/util/stopHandle';
import { type LoadGeometry, updateGeometryFromSource } from '@/lib/threeScene/nodes/meshGeometry';
import { plannerMaterialId } from '@/views/3d-planner/scene/plannerMaterial';
import { type PlannerMeshId, type PlannerObjectId } from '@/views/3d-planner/scene/plannerObjectId';
import { renderOrder } from '@/views/3d-planner/scene/renderOrder';
import type { PlannerSceneContext } from '@/views/3d-planner/scene/plannerSceneContext';
import { watchEffect } from 'vue';
import { isMeshVisible } from '@/views/3d-planner/scene/meshVisibility';

/** The common state representing a mesh in the scene */
export type PlannerMeshNode = MeshNode & {
    readonly plannerId: PlannerMeshId;
};

export function isPlannerMeshNode(node: MeshNode): node is PlannerMeshNode {
    return 'plannerId' in node;
}

export function plannerMeshNode(
    meshID: PlannerMeshId,
    properties?: Partial<PlannerMeshNode>,
): PlannerMeshNode {
    return {
        plannerId: meshID,
        ...meshNode(meshID, {
            name: meshID,
            ...properties,
            renderOrder: renderOrder[meshID],
        }),
    };
}

const _SINGLE_SIDED_MESHES: PlannerObjectId[] = ['cup-collision-surface', 'cup-coverage'] as const;

export function updatePlannerMesh(
    context: PlannerSceneContext,
    view: PlannerViewState,
    node: PlannerMeshNode,
    loadGeometry: LoadGeometry,
): StopHandle {

    return stopAll(
        updateMesh(context, node),
        watchEffect(() => node.visible = isMeshVisible(view.visibility, node.plannerId)),
        watchEffect(() => {
            switch (view.renderingMode) {
                case 'normal':
                    if (_SINGLE_SIDED_MESHES.includes(node.plannerId)) {
                        node.material = plannerMaterialId(node.plannerId, 'normal');
                    } else {
                        node.material = {
                            front: plannerMaterialId(node.plannerId, 'normal', 'frontside'),
                            back: plannerMaterialId(node.plannerId, 'normal', 'backside'),
                        };
                    }
                    break;
                case 'xray':
                    node.material = plannerMaterialId(node.plannerId, 'xray');
                    break;
                default:
                    throw new Error(`Unknown rendering-mode ${view.renderingMode}`);
            }
        }),
        // Geometry
        updateGeometryFromSource(node, loadGeometry),
    );
}
